import { useRef } from "react";
import { DashboardCharts } from "./components/DashboardCharts";
import { DashboardActivityFeed } from "./components/DashboardActivityFeed";
import { DashboardAI } from "./components/DashboardAI";
import { DashboardROI } from "./components/DashboardROI";
import { DailyBriefingCard } from "./components/DailyBriefingCard";
import { canSeeFinancialNavigation } from "@/components/navigation/app-navigation";
import { useAuth, useFetchSettingsTenantQuery, usePermission } from "@/hooks";
import { useDashboard } from "@/hooks/queries/dashboard/useDashboard";
import { useTenantId } from "@/hooks/use-tenant-id";
import { clinicGreeting } from "./greeting";
import { TimezoneSetupNotice } from "./components/TimezoneSetupNotice";
import {
  ArrowRight,
  Calendar,
  Flag,
  DollarSign,
  TrendingDown,
  Bot,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui";
import { KpiCard } from "@/components/ui/KpiCard";
import { TodayPriorities } from "./components/TodayPriorities";

export function Dashboard() {
  const reducedMotion = useReducedMotion();
  // Receives focus when a notice that held it leaves the page.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { user: currentUser } = useAuth();
  const { metrics, metricsLoading, metricsError, metricsFetching, refetchMetrics } = useDashboard();
  const { checkPermission } = usePermission();
  const canCreate = checkPermission("scheduling", "create scheduling");
  // P-02: the day and the greeting are read on the clinic's clock. Until the
  // clinic's timezone arrives the greeting is neutral ("Olá") and the date is
  // the browser's — the page never claims an hour it did not measure.
  const { data: clinicSettings } = useFetchSettingsTenantQuery();
  const timeZone = clinicSettings?.timezone;
  const now = new Date();
  const today = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    ...(timeZone ? { timeZone } : {}),
  }).format(now);
  const greeting = clinicGreeting(timeZone, now);
  const isAdmin = currentUser?.roles?.some((role) => role.name === "admin") ?? false;
  const tenantId = useTenantId(currentUser);

  return (
    // P-06: the shell owns the viewport height; <main> already scrolls.
    <div className="bg-app-bg">
      <motion.div
        className="space-y-8 py-5 sm:py-6"
        initial={reducedMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
      >
        <header className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm capitalize text-app-fg-muted">{today}</p>
            <h1 ref={headingRef} tabIndex={-1} className="mt-1 font-display text-[2.35rem] font-medium leading-[1.05] tracking-[-0.02em] text-app-fg-strong [text-wrap:balance] sm:text-[2.75rem]">
              {greeting}, {currentUser?.name?.split(" ")[0] || "Usuário"}
            </h1>
            <p className="mt-2 text-sm text-app-fg-muted sm:text-base">
              Veja o que pede atenção e como sua equipe está avançando.
            </p>
          </div>
          {canCreate && <Link
            to="/schedulings?openForm=1"
            className="inline-flex h-11 shrink-0 items-center justify-center gap-2 self-start rounded-lg bg-petrol px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-petrol-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 sm:self-auto"
          >
            <Plus className="size-4" aria-hidden="true" />
            Novo agendamento
          </Link>}
        </header>

        <TimezoneSetupNotice
          settings={clinicSettings}
          isAdmin={isAdmin}
          tenantId={tenantId}
          onDismiss={() => headingRef.current?.focus()}
        />

        {metricsError && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-app-border bg-app-surface p-4 text-sm text-app-fg">
          <p>Não foi possível atualizar os indicadores.</p>
          <Button variant="outline" disabled={metricsFetching} onClick={() => void refetchMetrics()}>Atualizar indicadores</Button>
        </div>}
        {metricsFetching && !metricsLoading && <p role="status" className="text-sm text-app-fg-muted">Atualizando indicadores…</p>}
        <TodayPriorities
          loading={metricsLoading}
          appointments={metrics?.appointmentsCount}
          highRiskPatients={metrics?.churnRisk?.high}
          aiEngagements={metrics?.aiEngagements}
        />

        {/* W2-10 (SCRUM-184): today's four headline numbers. The endpoint is
            `role:admin|accountant`, so the card is not drawn for a role that
            would only get a 403 out of it. C-21: this is the only briefing on
            Hoje — the second one described the same day from another endpoint. */}
        <DailyBriefingCard
          canRead={canSeeFinancialNavigation(
            (currentUser?.roles ?? []).map((role) => role.name)
          )}
        />

        {/* --- KPI Metrics Grid --- */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {(currentUser?.roles?.some((role) => ["admin", "accountant"].includes(role.name)) ?? false) && (
            <KpiCard
              label="Receita do Mês"
              icon={DollarSign}
              loading={metricsLoading}
              value={metrics?.revenue == null ? <span className="text-base">Indisponível</span> : new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(metrics.revenue)}
              helper={
                !metricsLoading && metrics?.revenue === 0
                  ? "Aparece após consultas pagas"
                  : undefined
              }
            />
          )}

          <KpiCard
            label="Agenda do mês"
            icon={Calendar}
            loading={metricsLoading}
            value={metrics?.appointmentsCount ?? <span className="text-base">Indisponível</span>}
            helper={
              !metricsLoading && metrics?.appointmentsCount === 0
                ? "Sem agendamentos ainda"
                : undefined
            }
          />

          <KpiCard
            label="Taxa de faltas"
            icon={TrendingDown}
            loading={metricsLoading}
            value={metrics?.noShowRate == null ? <span className="text-base">Indisponível</span> : `${metrics.noShowRate}%`}
            helper={
              !metricsLoading && metrics?.noShowRate === null
                ? "Faltas não são registradas separadamente"
                : undefined
            }
          />

          <KpiCard
            label="Atendimentos da IA"
            icon={Bot}
            loading={metricsLoading}
            value={metrics?.aiEngagements ?? <span className="text-base">Indisponível</span>}
            helper={metrics?.aiEngagements === 0 ? "Nenhum atendimento registrado no período" : undefined}
          />
        </div>

        {/* --- High Ticket ROI Dashboard Section --- */}
        {(currentUser?.roles?.some((role) => ["admin", "accountant"].includes(role.name)) ?? false) && (
          <DashboardROI />
        )}

        {/* --- Predictive Churn & Retention --- */}
        <section className="space-y-3">
          <header className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-2.5">
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
                <Flag className="size-3.5" strokeWidth={2.25} />
              </div>
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-app-fg-strong">
                  Risco de evasão (LTV)
                </h2>
                <p className="mt-1.5 text-sm text-app-fg-muted">
                  Pacientes agrupados pela frequência e pelo tempo desde o último atendimento.
                </p>
              </div>
            </div>
            <Link
              to="/patients"
              className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-brand transition-colors hover:text-brand-hover"
            >
              Ver pacientes
              <ArrowRight className="size-3.5" strokeWidth={2} />
            </Link>
          </header>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {[
              { key: "high", label: "Alto Risco", sublabel: "> 6 meses", dot: "bg-app-danger", value: metrics?.churnRisk?.high },
              { key: "medium", label: "Risco Médio", sublabel: "3 – 6 meses", dot: "bg-app-warning", value: metrics?.churnRisk?.medium },
              { key: "low", label: "Baixo Risco", sublabel: "Recentes", dot: "bg-app-success", value: metrics?.churnRisk?.low },
              { key: "uncalculated", label: "Não Calculado", sublabel: "Sem histórico", dot: "bg-app-fg-subtle", value: metrics?.churnRisk?.uncalculated },
            ].map((bucket) => (
              <div
                key={bucket.key}
                className="rounded-xl border border-app-border bg-app-surface p-5 shadow-premium-xs"
              >
                <div className="flex items-center gap-2">
                  <span className={`size-1.5 shrink-0 rounded-full ${bucket.dot}`} />
                  <p className="text-sm font-medium text-app-fg-muted">
                    {bucket.label}
                  </p>
                </div>
                {metricsLoading ? (
                  <div className="mt-3 h-7 w-12 animate-pulse rounded-md bg-app-surface-muted" />
                ) : (
                  <div className="mt-2 flex items-baseline gap-2">
                    <span
                      className="text-xl font-semibold leading-none tracking-tight text-app-fg-strong"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {bucket.value ?? <span className="text-base">Indisponível</span>}
                    </span>
                    <span className="text-xs text-app-fg-subtle">
                      {bucket.sublabel}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* --- Operational projections --- */}
        <DashboardAI />

        {/* --- Charts & Activity Grid --- */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <DashboardCharts />
          <DashboardActivityFeed />
        </div>

      </motion.div>
    </div>
  );
}
